/**
 * NodeIcon Component
 * Universal icon component that handles both Lucide and Simple Icons
 */

import React from 'react';
import { type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface NodeIconProps {
  /** Icon component (Lucide or Simple Icons) */
  icon: LucideIcon | React.ComponentType<any>;
  
  /** Type of icon system */
  iconType?: 'lucide' | 'simple-icon' | 'microsoft-icon' | 'integration-icon';
  
  /** Brand color (from Simple Icons) */
  iconColor?: string;
  
  /** Additional CSS classes */
  className?: string;
  
  /** Icon size in pixels */
  size?: number;
  
  /** Override color */
  color?: string;
}

/**
 * Universal icon component that handles both Lucide and Simple Icons
 * 
 * @example
 * // Lucide icon
 * <NodeIcon icon={Globe} iconType="lucide" className="h-5 w-5" />
 * 
 * @example
 * // Simple Icons with brand color
 * <NodeIcon 
 *   icon={SlackIcon} 
 *   iconType="simple-icon" 
 *   iconColor="#4A154B"
 *   size={20}
 * />
 */
export const NodeIcon: React.FC<NodeIconProps> = ({
  icon: Icon,
  iconType = 'lucide',
  iconColor,
  className,
  size = 20,
  color,
}) => {
  // Extract size from className if present (e.g., h-8 w-8 = 32px, h-10 w-10 = 40px)
  const extractSizeFromClassName = (className?: string): number => {
    if (!className) return size;
    
    // Match patterns like h-8, h-10, etc. (Tailwind sizing)
    const match = className.match(/h-(\d+)/);
    if (match) {
      const tailwindSize = parseInt(match[1]);
      // Tailwind uses 0.25rem units, so h-8 = 2rem = 32px
      return tailwindSize * 4;
    }
    
    return size;
  };
  
  const iconSize = extractSizeFromClassName(className);
  
  if (iconType === 'simple-icon') {
    // Simple Icons component from @icons-pack/react-simple-icons
    // These components accept size and color props
    return (
      <Icon
        size={iconSize}
        color={color || iconColor}
        className={cn('flex-shrink-0', className)}
        aria-hidden="true"
      />
    );
  }

  if (iconType === 'microsoft-icon') {
    // Microsoft custom SVG components accept size and color props
    return (
      <Icon
        size={iconSize}
        color={color || iconColor}
        className={cn('flex-shrink-0', className)}
        aria-hidden="true"
      />
    );
  }

  // Lucide icon component
  return (
    <Icon
      className={cn('flex-shrink-0', className)}
      style={{
        width: iconSize,
        height: iconSize,
        color: color,
      }}
      aria-hidden="true"
    />
  );
};
